<template>
    <div class="wizard-step">
        <b-row>
            <b-col cols="12">
                <StepIndicator :current-step="currentStep" :config="config" class="mb-5" @show-terms="$emit('show-terms')" />
                <h4>{{ idConfig.title || 'Verify Your Identity' }}</h4>
                <p class="text-muted">{{ idConfig.description }}</p>
            </b-col>
        </b-row>

        <!-- Already uploaded — show preview + replace -->
        <b-row class="mt-3" v-if="uploaded">
            <b-col cols="12">
                <b-card class="text-center upload-success">
                    <i class="fas fa-check-circle text-success mb-3" style="font-size: 56px;"></i>
                    <h5 class="mb-3">ID received</h5>
                    <div class="preview-wrapper mb-3" :class="{ rotating: rotating }">
                        <img :src="previewUrl" alt="Uploaded ID preview" class="preview-image"/>
                        <div class="rotating-overlay" v-if="rotating">
                            <i class="fas fa-sync-alt fa-spin"></i> Rotating…
                        </div>
                    </div>
                    <p v-if="rotateError" class="text-danger small mb-2">{{ rotateError }}</p>
                    <div class="preview-actions">
                        <!-- A blocked photo needs a retake, not a rotate — and each
                             rotate re-validates, which would burn a blocked attempt. -->
                        <b-button v-if="!blocked" variant="primary" @click="rotateImage" :disabled="rotating">
                            <i class="fas fa-redo-alt mr-1"></i>Rotate
                        </b-button>
                        <b-button variant="outline-primary" @click="resetUpload" :disabled="rotating">
                            <i class="fas fa-camera-retro mr-1"></i>Upload a different photo
                        </b-button>
                    </div>

                    <!-- AI Review — identity failures block until retaken; quality issues warn only -->
                    <div class="ai-review mt-3" v-if="aiReviewVisible">
                        <div v-if="aiChecking" class="ai-checking">
                            <i class="fas fa-sync-alt fa-spin mr-2"></i>Checking your photo…
                        </div>
                        <div v-else-if="blocked" class="ai-blocked">
                            <div class="ai-blocked-header">
                                <i class="fas fa-times-circle mr-2"></i>
                                <strong>We couldn't accept this photo</strong>
                            </div>
                            <ul class="ai-issues mb-2 mt-2">
                                <li v-for="msg in customerIssueMessages" :key="msg">{{ msg }}</li>
                            </ul>
                            <p class="ai-hint small mb-0">Please upload a new, clear photo of the front of your government-issued ID to continue.</p>
                        </div>
                        <div v-else-if="bypassed" class="ai-warned">
                            <div class="ai-warned-header">
                                <i class="fas fa-exclamation-triangle mr-2"></i>
                                <strong>We couldn't verify this photo</strong>
                            </div>
                            <p class="ai-hint small mb-1 mt-2">You can continue, and our team will review your ID manually.</p>
                            <p class="ai-hint small mb-0">An invalid or unreadable ID may delay or prevent approval, so if you can take a clearer photo, please do.</p>
                        </div>
                        <div v-else-if="validation && validation.status === 'passed'" class="ai-passed">
                            <i class="fas fa-check-circle mr-2"></i>Looks good
                        </div>
                        <div v-else-if="validation && validation.status === 'warned'" class="ai-warned">
                            <div class="ai-warned-header">
                                <i class="fas fa-exclamation-triangle mr-2"></i>
                                <strong>A few things to double-check</strong>
                            </div>
                            <ul class="ai-issues mb-2 mt-2">
                                <li v-for="msg in customerIssueMessages" :key="msg">{{ msg }}</li>
                            </ul>
                            <p class="ai-hint small mb-1">This automated review can be inaccurate. If your photo is clear and the information is correct, feel free to continue.</p>
                            <p class="ai-hint small mb-0">If you do continue, please make sure the photo is correct — an inaccurate or unreadable ID may cause your application to be denied.</p>
                        </div>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <!-- Mobile — direct camera/file capture -->
        <b-row class="mt-3" v-else-if="isMobile">
            <b-col cols="12">
                <b-card class="text-center mobile-capture">
                    <i class="fas fa-camera text-primary mb-3" style="font-size: 64px;"></i>
                    <h5 class="mb-3">Take a photo of your ID</h5>
                    <p class="text-muted mb-3">Place the <strong>front</strong> of your driver's license or state ID on a flat surface with good lighting and make sure all four corners are visible. We only need the front side.</p>

                    <b-alert show variant="info" class="text-left rotate-tip mb-4">
                        <i class="fas fa-mobile-alt fa-rotate-90 mr-2"></i>
                        <strong>Turn your phone sideways</strong> before snapping the photo so the ID card fills the frame.
                    </b-alert>

                    <label class="btn btn-primary btn-lg btn-block capture-button" :class="{ disabled: uploading }">
                        <i class="fas fa-camera mr-2"></i>{{ uploading ? 'Uploading…' : 'Open camera' }}
                        <input type="file" accept="image/*" capture="environment" class="d-none" @change="onFileSelected" :disabled="uploading"/>
                    </label>

                    <label class="btn btn-outline-primary btn-block mt-2" :class="{ disabled: uploading }">
                        <i class="fas fa-image mr-2"></i>Choose from photos
                        <input type="file" accept="image/*" class="d-none" @change="onFileSelected" :disabled="uploading"/>
                    </label>

                    <p v-if="errorMessage" class="text-danger mt-3 mb-0">{{ errorMessage }}</p>
                </b-card>
            </b-col>
        </b-row>

        <!-- Desktop — SMS link + drag-drop file upload -->
        <b-row class="mt-3" v-else>
            <b-col cols="12" md="6" class="mb-3">
                <b-card class="h-100 sms-card">
                    <div class="text-center">
                        <i class="fas fa-mobile-alt text-primary mb-3" style="font-size: 48px;"></i>
                        <h5>Text a link to my phone</h5>
                        <p class="text-muted" v-if="phoneHint">We'll send a one-time link to your phone ending in {{ phoneHint }}.</p>
                        <p class="text-muted" v-else>We don't have a phone number on file for this option.</p>

                        <b-alert :show="!!smsStatus.message" :variant="smsStatus.variant" class="mt-3 text-left">
                            {{ smsStatus.message }}
                        </b-alert>

                        <b-button
                            variant="primary"
                            :disabled="!phoneHint || smsSending || smsCooldown"
                            @click="sendIdLink">
                            <i class="fas fa-paper-plane mr-2"></i>
                            <span v-if="smsSending">Sending…</span>
                            <span v-else-if="smsCooldown">Sent — check your phone</span>
                            <span v-else>Text me the link</span>
                        </b-button>
                        <p class="text-muted small mt-3 mb-0" v-if="smsCooldown">
                            Didn't get it? You can re-send in {{ smsCooldownRemaining }}s.
                        </p>
                    </div>
                </b-card>
            </b-col>

            <b-col cols="12" md="6" class="mb-3">
                <b-card class="h-100 upload-card">
                    <div class="text-center">
                        <i class="fas fa-upload text-primary mb-3" style="font-size: 48px;"></i>
                        <h5>Upload from this device</h5>
                        <p class="text-muted">Choose an image file (JPG, PNG, HEIC). Max 10 MB.</p>

                        <file-pond
                            ref="pond"
                            name="image"
                            label-idle='Drop an ID image here or <span class="filepond--label-action">click to browse</span>'
                            :accepted-file-types="['image/jpeg', 'image/png', 'image/heic', 'image/heif']"
                            :allow-multiple="false"
                            :max-file-size="'10MB'"
                            :server="filePondServer"
                            @processfile="onPondProcessed"
                            @error="onPondError"
                        />
                        <p v-if="errorMessage" class="text-danger mt-3 mb-0">{{ errorMessage }}</p>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <!-- Post-SMS instructions while waiting for phone upload (desktop only) -->
        <b-row class="mt-3" v-if="!uploaded && smsLinkSent && !isMobile">
            <b-col cols="12">
                <b-card class="waiting-card" v-if="!smsLinkExpired">
                    <h6 class="mb-3"><i class="fas fa-mobile-alt text-primary mr-2"></i>What to do on your phone</h6>
                    <ol class="mb-3 instruction-list">
                        <li>Open the text message from The Payment Option.</li>
                        <li>Tap the link to open the secure upload page.</li>
                        <li>Take a clear photo of your driver's license or state ID.</li>
                        <li>Come back here — we'll show <strong>ID received</strong> as soon as it lands.</li>
                    </ol>
                    <div class="d-flex align-items-center text-muted">
                        <i class="fas fa-sync-alt fa-spin mr-2"></i>
                        <small>Watching for your upload — link expires in {{smsLinkMinutesRemaining}} {{smsLinkMinutesRemaining === 1 ? 'minute' : 'minutes'}}.</small>
                    </div>
                </b-card>
                <b-card class="expired-card" v-else>
                    <div class="d-flex align-items-center mb-2">
                        <i class="fas fa-clock text-warning mr-2"></i>
                        <strong>The link sent to your phone has expired.</strong>
                    </div>
                    <p class="text-muted small mb-0">No worries — you can send a fresh one above, or upload from this device.</p>
                </b-card>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import {apiUrl} from '@/constants/config';
import axios from 'axios';
import StepIndicator from '@/components/sign-agreement/StepIndicator.vue';
import { isMobileDevice } from '@/utils/device';
import { describeValidationIssues } from '@/utils/idValidationMessages';
import vueFilePond from 'vue-filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';

const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginImagePreview);

const POLL_INTERVAL_MS = 3000;
const SMS_COOLDOWN_S = 30;
// How long to hold the step invalid while waiting for the AI result before
// failing open. The server-side signing check remains the real backstop.
const AI_CHECK_TIMEOUT_MS = 20000;

export default {
    name: 'IdVerification',
    components: { StepIndicator, FilePond },
    props: {
        apiPath: { type: String, required: true },
        currentStep: { type: Number, required: true },
        config: { type: Object, default: () => ({}) },
        verifiedSms: { type: String, default: '' },
        initialUploadId: { type: [Number, String], default: null }
    },
    data() {
        return {
            isMobile: isMobileDevice(),
            uploaded: !!this.initialUploadId,
            uploadedSource: null,
            lastSeenUploadId: this.initialUploadId ? String(this.initialUploadId) : null,
            previewBust: Date.now(),
            uploading: false,
            errorMessage: '',
            smsSending: false,
            smsCooldown: false,
            smsCooldownRemaining: 0,
            smsCooldownTimer: null,
            smsLinkSent: false,
            smsLinkExpiresAt: 0,
            now: Date.now(),
            linkTickTimer: null,
            pollTimer: null,
            smsStatus: { variant: 'info', message: '' },
            rotating: false,
            rotateError: '',
            validation: null,
            gate: null,
            aiChecking: false,
            aiCheckTimer: null
        };
    },
    computed: {
        idConfig() {
            return (this.config && this.config.idVerification) || {};
        },
        smsLinkExpired() {
            return this.smsLinkExpiresAt > 0 && this.now >= this.smsLinkExpiresAt;
        },
        smsLinkMinutesRemaining() {
            if (!this.smsLinkExpiresAt) return 0;
            const ms = this.smsLinkExpiresAt - this.now;
            return ms > 0 ? Math.ceil(ms / 60000) : 0;
        },
        aiReviewVisible() {
            // Hide the AI review block entirely if validation returned an error or is unconfigured.
            if (this.aiChecking) return true;
            if (!this.validation) return false;
            if (this.validation.status === 'error') return false;
            return this.validation.status === 'passed' || this.validation.status === 'warned';
        },
        blocked() {
            return !!(this.gate && this.gate.blocked && !this.gate.bypassAllowed);
        },
        bypassed() {
            return !!(this.gate && this.gate.blocked && this.gate.bypassAllowed);
        },
        stepValid() {
            // The gate arrives with every upload/status response; requiring it
            // keeps a returning visitor from advancing before we know whether
            // their existing upload was blocked.
            return this.uploaded && !this.aiChecking && !!this.gate && !this.blocked;
        },
        customerIssueMessages() {
            if (!this.validation) return [];
            const out = describeValidationIssues(this.validation);
            // If there were no specific slugs but the model still warned, fall back to a generic message
            if (out.length === 0 && this.validation.status === 'warned') {
                out.push('The photo could be clearer — consider retaking it.');
            }
            return out;
        },
        phoneHint() {
            const digits = String(this.verifiedSms || '').replace(/[^0-9]/g, '');
            if (digits.length < 4) return '';
            return digits.slice(-4);
        },
        previewUrl() {
            return `${apiUrl}${this.apiPath}/id-upload-image?v=${this.previewBust}`;
        },
        filePondServer() {
            const self = this;
            return {
                process: (fieldName, file, metadata, load, error, progress, abort) => {
                    const formData = new FormData();
                    formData.append('image', file, file.name);
                    self.uploading = true;
                    self.errorMessage = '';
                    self.$emit('audit-event', { eventType: 'id_upload_initiated', data: { source: 'desktop' } });
                    axios.post(`${apiUrl}${self.apiPath}/upload-id`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                        onUploadProgress: (e) => {
                            if (e.lengthComputable) progress(true, e.loaded, e.total);
                        }
                    })
                    .then(res => {
                        self.uploading = false;
                        self.handleUploadSuccess(res.data, 'desktop', true);
                        load(String(res.data.uploadId || file.name));
                    })
                    .catch(err => {
                        self.uploading = false;
                        const msg = (err && err.response && err.response.data && err.response.data.error) || 'upload_failed';
                        self.errorMessage = self.friendlyUploadError(msg);
                        error(msg);
                    });
                    return { abort };
                }
            };
        }
    },
    watch: {
        stepValid: {
            immediate: true,
            handler(isValid) {
                this.$emit('validation-changed', isValid);
            }
        }
    },
    mounted() {
        if (this.uploaded) {
            this.refreshStatus();
        }
        this.startPolling();
    },
    beforeDestroy() {
        this.stopPolling();
        if (this.smsCooldownTimer) clearInterval(this.smsCooldownTimer);
        if (this.linkTickTimer) clearInterval(this.linkTickTimer);
        if (this.aiCheckTimer) clearTimeout(this.aiCheckTimer);
    },
    methods: {
        startPolling() {
            this.stopPolling();
            this.pollTimer = setInterval(this.refreshStatus, POLL_INTERVAL_MS);
        },
        stopPolling() {
            if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
        },
        startLinkTick() {
            if (this.linkTickTimer) clearInterval(this.linkTickTimer);
            // Tick once per 15 seconds — coarse enough not to burn cycles, fine
            // enough that "expires in N minutes" feels live as the clock counts down.
            this.linkTickTimer = setInterval(() => {
                this.now = Date.now();
                if (this.smsLinkExpired && !this.uploaded) {
                    // The token is dead on the server. Stop everything quietly.
                    this.stopPolling();
                    clearInterval(this.linkTickTimer);
                    this.linkTickTimer = null;
                }
            }, 15000);
        },
        async refreshStatus() {
            try {
                const res = await axios.get(`${apiUrl}${this.apiPath}/id-upload-status`);
                if (!res.data || !res.data.uploaded) return;
                const newId = String(res.data.uploadId);
                // Ignore a status response that just confirms the upload we already showed
                // (or the one the user just chose to replace).
                if (this.lastSeenUploadId && newId === this.lastSeenUploadId) {
                    // Same upload — but a validation may have just landed for it,
                    // and the gate is what decides whether the step is valid.
                    if (res.data.gate) {
                        this.gate = res.data.gate;
                    } else if (!this.gate) {
                        // Server build without gate support — fail open, like the
                        // old warn-only behavior it implements.
                        this.gate = { blocked: false };
                    }
                    if (!this.validation && res.data.validation) {
                        this.applyValidation(res.data.validation);
                    }
                    // Settled and acceptable — nothing left for the poll to
                    // deliver. (While blocked we keep polling: the customer may
                    // retake from the still-live phone link.)
                    if (this.uploaded && !this.aiChecking && !this.blocked) {
                        this.stopPolling();
                    }
                    return;
                }
                // Ignore stale, out-of-order responses about an upload older than
                // the one we already show (upload ids are monotonically increasing).
                if (this.lastSeenUploadId && Number(newId) < Number(this.lastSeenUploadId)) {
                    return;
                }

                const wasUploaded = this.uploaded;
                this.handleUploadSuccess(res.data, res.data.source);
                if (!wasUploaded && res.data.source === 'phone_sms') {
                    this.smsStatus = { variant: 'success', message: 'We received your ID from your phone!' };
                }
            } catch (e) {
                // soft-fail; poll will retry
            }
        },
        // validationIsFinal: an upload POST response carries the validation the
        // server computed synchronously — if it's absent there, it will never
        // arrive (no API key, or the check errored), so don't wait on it. Only
        // a poll-discovered phone upload can have a validation still in flight.
        handleUploadSuccess(data, source, validationIsFinal = false) {
            this.uploaded = true;
            this.uploadedSource = source || data.source || null;
            this.lastSeenUploadId = data.uploadId ? String(data.uploadId) : this.lastSeenUploadId;
            this.previewBust = Date.now();
            this.errorMessage = '';
            // The link's purpose is done — kill the expiry tick so we don't keep
            // re-rendering minute counts on a card the user no longer sees.
            if (this.linkTickTimer) { clearInterval(this.linkTickTimer); this.linkTickTimer = null; }
            this.smsLinkExpiresAt = 0;
            // Missing gate (server build without gate support) fails open — the
            // signing endpoint is the backstop.
            this.gate = data.gate || { blocked: false };
            this.validation = null;
            this.clearAiCheck();
            if (data.validation) {
                this.applyValidation(data.validation);
            } else if (!validationIsFinal) {
                this.startAiCheckWindow();
            }
            this.stopPolling();
            // Keep watching the server while a validation is still in flight, or
            // while blocked — a blocked phone upload leaves its SMS link alive,
            // so a passing retake can land without any action on this screen.
            if (this.aiChecking || this.blocked) this.startPolling();
            this.$emit('audit-event', {
                eventType: 'id_upload_completed',
                data: { uploadId: data.uploadId, source: this.uploadedSource }
            });
        },
        applyValidation(validation) {
            if (!validation) return;
            this.validation = validation;
            this.clearAiCheck();
        },
        startAiCheckWindow() {
            // Hold the step invalid while we wait for the result, then fail
            // open (the server-side signing check is the backstop if a
            // blocking result lands late).
            this.aiChecking = true;
            this.aiCheckTimer = setTimeout(() => {
                this.aiChecking = false;
                this.aiCheckTimer = null;
            }, AI_CHECK_TIMEOUT_MS);
        },
        clearAiCheck() {
            this.aiChecking = false;
            if (this.aiCheckTimer) { clearTimeout(this.aiCheckTimer); this.aiCheckTimer = null; }
        },
        resetUpload() {
            this.uploaded = false;
            this.uploadedSource = null;
            // Keep lastSeenUploadId set so polling won't immediately re-flag the current
            // server-side upload as "received" — we only want to react to a NEW upload.
            this.smsLinkSent = false;
            this.smsLinkExpiresAt = 0;
            if (this.linkTickTimer) { clearInterval(this.linkTickTimer); this.linkTickTimer = null; }
            this.smsStatus = { variant: 'info', message: '' };
            this.validation = null;
            this.gate = null;
            this.clearAiCheck();
            this.startPolling();
            this.$nextTick(() => {
                if (this.$refs.pond) this.$refs.pond.removeFiles();
            });
        },
        onFileSelected(event) {
            const file = event.target.files && event.target.files[0];
            if (!file) return;
            this.uploadFile(file);
            event.target.value = '';
        },
        async uploadFile(file) {
            this.uploading = true;
            this.errorMessage = '';
            this.$emit('audit-event', { eventType: 'id_upload_initiated', data: { source: this.isMobile ? 'mobile_inline' : 'desktop' } });
            try {
                const formData = new FormData();
                formData.append('image', file, file.name || 'id.jpg');
                const res = await axios.post(`${apiUrl}${this.apiPath}/upload-id`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                this.handleUploadSuccess(res.data, 'desktop', true);
            } catch (err) {
                const msg = (err && err.response && err.response.data && err.response.data.error) || 'upload_failed';
                this.errorMessage = this.friendlyUploadError(msg);
            } finally {
                this.uploading = false;
            }
        },
        async rotateImage() {
            if (this.rotating) return;
            this.rotating = true;
            this.rotateError = '';
            try {
                const resp = await axios.get(`${apiUrl}${this.apiPath}/id-upload-image`, { responseType: 'blob' });
                const blob = resp.data;

                // Use createImageBitmap so EXIF orientation is baked into the pixels
                // before we rotate. Fall back to a regular Image element if needed.
                let source;
                if (typeof createImageBitmap === 'function') {
                    try {
                        source = await createImageBitmap(blob, { imageOrientation: 'from-image' });
                    } catch (e) {
                        source = await createImageBitmap(blob);
                    }
                } else {
                    source = await new Promise((resolve, reject) => {
                        const img = new Image();
                        const url = URL.createObjectURL(blob);
                        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
                        img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
                        img.src = url;
                    });
                }

                const srcW = source.width || source.naturalWidth;
                const srcH = source.height || source.naturalHeight;

                const canvas = document.createElement('canvas');
                canvas.width = srcH;
                canvas.height = srcW;
                const ctx = canvas.getContext('2d');
                ctx.translate(canvas.width / 2, canvas.height / 2);
                ctx.rotate(Math.PI / 2);
                ctx.drawImage(source, -srcW / 2, -srcH / 2);

                const rotatedBlob = await new Promise((resolve, reject) => {
                    canvas.toBlob(b => b ? resolve(b) : reject(new Error('canvas_toBlob_failed')), 'image/jpeg', 0.92);
                });

                const formData = new FormData();
                formData.append('image', rotatedBlob, 'id_rotated.jpg');
                formData.append('is_rotation', '1');
                const uploadRes = await axios.post(`${apiUrl}${this.apiPath}/upload-id`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                this.handleUploadSuccess(uploadRes.data, uploadRes.data.source || this.uploadedSource, true);
                this.$emit('audit-event', { eventType: 'id_upload_rotated', data: { uploadId: uploadRes.data.uploadId } });
            } catch (err) {
                console.warn('rotateImage failed:', err);
                this.rotateError = 'Couldn\'t rotate the image. Try uploading a different photo.';
            } finally {
                this.rotating = false;
            }
        },
        async sendIdLink() {
            if (this.smsSending || this.smsCooldown || !this.phoneHint) return;
            this.smsSending = true;
            this.errorMessage = '';
            this.smsStatus = { variant: 'info', message: '' };
            try {
                const res = await axios.post(`${apiUrl}${this.apiPath}/send-id-upload-link`, {});
                this.smsStatus = {
                    variant: 'success',
                    message: `Link sent to your phone ending in ${res.data.phoneLastFour}. It expires in ${res.data.expiresInMinutes} minutes.`
                };
                this.smsLinkSent = true;
                this.smsLinkExpiresAt = Date.now() + (Number(res.data.expiresInMinutes) || 30) * 60000;
                this.now = Date.now();
                this.startLinkTick();
                this.startPolling();  // ensure polling is running while we wait
                this.$emit('audit-event', { eventType: 'id_link_sms_sent', data: { phoneLastFour: res.data.phoneLastFour } });
                this.startSmsCooldown();
            } catch (err) {
                const code = (err && err.response && err.response.data && err.response.data.error) || '';
                if (code === 'too_many_sms') {
                    this.smsStatus = { variant: 'warning', message: 'Too many text messages sent recently. Please try again in an hour or upload from this device instead.' };
                } else if (code === 'no_phone_available') {
                    this.smsStatus = { variant: 'warning', message: 'No phone number on file — please upload from this device.' };
                } else {
                    this.smsStatus = { variant: 'danger', message: 'Couldn\'t send the link. Please try again or upload from this device.' };
                }
            } finally {
                this.smsSending = false;
            }
        },
        startSmsCooldown() {
            this.smsCooldown = true;
            this.smsCooldownRemaining = SMS_COOLDOWN_S;
            if (this.smsCooldownTimer) clearInterval(this.smsCooldownTimer);
            this.smsCooldownTimer = setInterval(() => {
                this.smsCooldownRemaining -= 1;
                if (this.smsCooldownRemaining <= 0) {
                    clearInterval(this.smsCooldownTimer);
                    this.smsCooldownTimer = null;
                    this.smsCooldown = false;
                }
            }, 1000);
        },
        onPondProcessed() { /* no-op — handled in server.process callback */ },
        onPondError(err) {
            const msg = (err && err.body) || '';
            this.errorMessage = this.friendlyUploadError(msg);
        },
        friendlyUploadError(code) {
            switch (code) {
                case 'no_file': return 'No file was selected.';
                case 'unsupported_image_type': return 'That file type isn\'t supported. Use JPG, PNG, or HEIC.';
                case 'plan_not_found': return 'We couldn\'t find your agreement — please refresh the page.';
                case 'already_onboarded': return 'This agreement has already been completed.';
                default: return 'Upload failed. Please try a different photo.';
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.upload-success {
    .preview-wrapper {
        position: relative;
        display: flex;
        justify-content: center;
        &.rotating .preview-image {
            opacity: 0.4;
        }
    }
    .preview-image {
        max-width: 320px;
        max-height: 240px;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        transition: opacity 0.15s ease;
    }
    .rotating-overlay {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(0, 0, 0, 0.65);
        color: white;
        padding: 8px 14px;
        border-radius: 6px;
        font-size: 0.9rem;
    }
    .preview-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        justify-content: center;
    }
}
.mobile-capture .capture-button {
    padding: 18px;
    font-size: 1.1rem;
    cursor: pointer;
    label.disabled {
        opacity: 0.6;
        pointer-events: none;
    }
}
label.btn input[type="file"] {
    display: none;
}
.sms-card, .upload-card {
    border-left: 4px solid #464577;
}
.waiting-card {
    background: #f0f7ff;
    border-left: 4px solid #0d6efd;
    .instruction-list {
        padding-left: 1.25rem;
        li { margin-bottom: 0.4rem; }
    }
}
.expired-card {
    background: #fff8e1;
    border-left: 4px solid #f0ad4e;
    color: #6b4f00;
}
.ai-review {
    border-top: 1px solid #eef0f4;
    padding-top: 0.85rem;
    text-align: left;
}
.ai-checking {
    color: #6c757d;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    justify-content: center;
}
.ai-passed {
    color: #1c7430;
    font-size: 0.95rem;
    background: #e6f4ea;
    border-radius: 6px;
    padding: 8px 12px;
    display: inline-flex;
    align-items: center;
}
.ai-blocked {
    background: #fdecea;
    border-left: 4px solid #dc3545;
    border-radius: 6px;
    padding: 10px 14px;
    color: #7a1f28;
    .ai-blocked-header {
        font-size: 0.95rem;
        display: flex;
        align-items: center;
    }
    .ai-issues {
        padding-left: 1.25rem;
        margin-bottom: 0.25rem;
        li { margin-bottom: 0.25rem; }
    }
}
.ai-warned {
    background: #fff8e1;
    border-left: 4px solid #f0ad4e;
    border-radius: 6px;
    padding: 10px 14px;
    color: #6b4f00;
    .ai-warned-header {
        font-size: 0.95rem;
        display: flex;
        align-items: center;
    }
    .ai-issues {
        padding-left: 1.25rem;
        margin-bottom: 0.25rem;
        li { margin-bottom: 0.25rem; }
    }
}
</style>
